import React, { useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { 
  LayoutDashboard, 
  Mail, 
  Calendar, 
  Receipt, 
  FileText, 
  CheckSquare, 
  Users, 
  ShieldCheck, 
  Settings, 
  ChevronLeft, 
  ChevronRight,
  Plus,
  PenTool,
  Sliders
} from 'lucide-react';

const Navigation = ({ activeTab, setActiveTab, isOpen, setIsOpen }) => {
  const { currentUser, deptLeads, secondaryApproverIds, currentLogo } = useContext(AppContext);

  // Check if current user is an approver (dept lead or secondary approver)
  const isApprover = currentUser && (
    Object.values(deptLeads || {}).includes(currentUser.UserID) || 
    (secondaryApproverIds || []).includes(currentUser.UserID)
  );

  // Dispatch custom window events to trigger forms or modals inside active tabs
  const handleSidebarAction = () => {
    if (activeTab === 'webmail' || activeTab === 'dashboard') {
      window.dispatchEvent(new Event('trigger-compose'));
    } else if (activeTab === 'leave') {
      window.dispatchEvent(new Event('trigger-apply-leave'));
    } else if (activeTab === 'expense') {
      window.dispatchEvent(new Event('trigger-apply-expense'));
    } else if (activeTab === 'admin') {
      window.dispatchEvent(new Event('trigger-system-settings'));
    } else if (activeTab === 'addressbook') {
      window.dispatchEvent(new Event('trigger-add-customer'));
    } else if (activeTab === 'settings') {
      window.dispatchEvent(new Event('trigger-save-profile'));
    }
    if (window.innerWidth < 768) {
      setIsOpen(false); // Close sidebar on mobile after action
    }
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (!isOpen && window.innerWidth >= 1024) {
      setIsOpen(true); // Auto-open sub-sidebar on tab switch if desktop
    }
    if (window.innerWidth < 768) {
      setIsOpen(false); // Close sidebar on mobile
    }
  };

  // Nav items configuration for Dark Rail & Sub-Sidebar
  const navItems = [
    { id: 'dashboard', label: '대시보드', icon: LayoutDashboard, subTitle: '종합 상황실 & 위젯' },
    { id: 'webmail', label: '웹메일', icon: Mail, subTitle: '편지함 & AI 요약' },
    { id: 'leave', label: '연차/휴가', icon: Calendar, subTitle: '신청 및 잔여 관리' },
    { id: 'expense', label: '경비처리', icon: Receipt, subTitle: 'AI OCR 영수증 청구' },
    { id: 'contract', label: '전자계약', icon: FileText, subTitle: '사내 서명 & 계약서' },
    ...(currentUser?.Role !== 'ADMIN' && isApprover ? [
      { id: 'approval', label: '결재 승인', icon: CheckSquare, subTitle: '연차/경비 승인' }
    ] : []),
    ...(currentUser?.Role === 'ADMIN' ? [
      { id: 'admin', label: '관리자 센터', icon: ShieldCheck, subTitle: '사원/시스템/설정' }
    ] : []),
    { id: 'addressbook', label: '주소록', icon: Users, subTitle: '임직원 & 고객 연락처' },
    { id: 'settings', label: '환경설정', icon: Settings, subTitle: '개인 프로필 & 보안' },
  ];

  const currentTabObj = navItems.find(item => item.id === activeTab) || navItems[0];
  const userInitial = (currentUser?.Name || currentUser?.Account || 'U').slice(0, 1).toUpperCase();

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-xs md:hidden"
        />
      )}

      {/* 1. Left 64px Dark Icon Rail (항상 노출되는 최좌측 다크 레일) */}
      <aside className="w-16 bg-[#161922] border-r border-[#2B3040] flex flex-col items-center justify-between py-4 select-none shrink-0 z-50 h-screen sticky top-0">
        {/* Top: Gradient Brand Logo */}
        <div className="flex flex-col items-center gap-6">
          <div 
            onClick={() => handleTabClick('dashboard')}
            className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center font-black text-white text-lg shadow-lg shadow-indigo-500/25 cursor-pointer hover:scale-105 transition-all"
            title="클로우플레이크 홈으로 이동"
          >
            C
          </div>

          {/* Navigation Icon Icons */}
          <nav className="flex flex-col items-center gap-2.5">
            {navItems.map(item => {
              const IconComponent = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabClick(item.id)}
                  title={`${item.label} (${item.subTitle})`}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all cursor-pointer relative group ${
                    isActive
                      ? 'bg-[#5C5FEF] text-white shadow-md shadow-indigo-500/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/80'
                  }`}
                >
                  <IconComponent className="w-5 h-5 stroke-[2.2]" />
                  {/* Tooltip on hover */}
                  <span className="absolute left-14 bg-slate-900 text-white text-xs font-bold px-2.5 py-1 rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-xl border border-slate-700 z-50">
                    {item.label}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Bottom Profile Dock & Sub-Sidebar Toggle */}
        <div className="flex flex-col items-center gap-3">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-9 h-9 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 flex items-center justify-center transition-all cursor-pointer"
            title={isOpen ? "서브 메뉴 접기" : "서브 메뉴 펼치기"}
          >
            {isOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          
          <div 
            onClick={() => handleTabClick('settings')}
            className="w-9 h-9 rounded-full bg-indigo-600/30 text-indigo-300 font-black border border-indigo-400/40 flex items-center justify-center text-xs cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-all shadow-inner"
            title={`${currentUser?.Name || '사용자'} (${currentUser?.Department || '클로우'})`}
          >
            {userInitial}
          </div>
        </div>
      </aside>

      {/* 2. Left 240px Dark Sub-Sidebar (접고 펼칠 수 있는 다크 서브 사이드바) */}
      <aside
        className={`fixed md:sticky top-0 left-16 z-40 h-screen bg-[#1D212C] border-r border-[#2B3040] flex flex-col justify-between transform transition-all duration-300 ease-in-out shrink-0 select-none ${
          isOpen
            ? 'w-60 p-4 translate-x-0 opacity-100'
            : '-translate-x-full md:translate-x-0 md:w-0 md:p-0 md:border-none md:overflow-hidden md:opacity-0 pointer-events-none'
        }`}
      >
        <div className="space-y-5 min-w-[208px]">
          {/* Header Brand & Section Title */}
          <div className="pb-4 border-b border-[#2B3040] flex items-center justify-between px-1">
            <div className="flex items-center gap-2 bg-white px-2.5 py-1.5 rounded-xl border border-slate-700/40 shadow-xs">
              <img
                src={currentLogo}
                alt="Clouflake"
                className="object-contain cursor-pointer transition-transform hover:scale-105 duration-200 max-h-7"
                style={{ width: 'var(--logo-pc-width)', height: 'var(--logo-pc-height)' }}
                onClick={() => handleTabClick('dashboard')}
              />
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg cursor-pointer transition-colors"
              title="사이드바 닫기"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>

          {/* Sub-menu title banner */}
          <div className="px-2 py-1 flex items-center justify-between">
            <span className="text-[11px] font-extrabold tracking-wider uppercase text-slate-400">
              {currentTabObj.label} 메뉴
            </span>
            <span className="text-[10px] text-indigo-400 font-bold bg-indigo-950/60 border border-indigo-800/60 px-1.5 py-0.5 rounded">
              AX
            </span>
          </div>

          {/* Navigation Links with Modern Active Highlights */}
          <nav className="space-y-1 text-left">
            {navItems.map(item => {
              const IconComponent = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabClick(item.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-bold transition-all duration-150 cursor-pointer ${
                    isActive
                      ? 'bg-[#5C5FEF] text-white shadow-md shadow-indigo-500/20'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800/70'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <IconComponent className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                    <span>{item.label}</span>
                  </div>
                  <span className={`text-[10px] font-medium ${isActive ? 'text-indigo-200' : 'text-slate-500'}`}>
                    {item.id === 'webmail' ? '메일함' : item.id === 'leave' ? '연차' : item.id === 'expense' ? '경비' : ''}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Sub-Sidebar Bottom Action Button (Solid Indigo CTA) */}
        <div className="min-w-[208px] pt-3 border-t border-[#2B3040]">
          <button
            onClick={handleSidebarAction}
            className="w-full py-2.5 px-3 bg-[#5C5FEF] hover:bg-[#4E51E2] text-white font-extrabold text-xs rounded-xl shadow-lg shadow-indigo-500/25 transition-all cursor-pointer flex items-center justify-center gap-1.5 active:scale-98"
          >
            {activeTab === 'dashboard' && <><PenTool className="w-3.5 h-3.5" /><span>새 메일 작성</span></>}
            {activeTab === 'webmail' && <><PenTool className="w-3.5 h-3.5" /><span>새 메일 작성</span></>}
            {activeTab === 'leave' && <><Plus className="w-3.5 h-3.5" /><span>연차 신청</span></>}
            {activeTab === 'expense' && <><Plus className="w-3.5 h-3.5" /><span>경비 등록</span></>}
            {activeTab === 'approval' && <><CheckSquare className="w-3.5 h-3.5" /><span>결재 승인</span></>}
            {activeTab === 'admin' && <><Sliders className="w-3.5 h-3.5" /><span>시스템 설정</span></>}
            {activeTab === 'addressbook' && <><Plus className="w-3.5 h-3.5" /><span>연락처 등록</span></>}
            {activeTab === 'contract' && <><FileText className="w-3.5 h-3.5" /><span>새 계약 작성</span></>}
            {activeTab === 'settings' && <><Settings className="w-3.5 h-3.5" /><span>프로필 저장</span></>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Navigation;
